"use client";

import { useEffect, useState } from "react";
import type { Locale } from "@/lib/i18n/config";
import { useAdFormatArm } from "@/lib/monetization/ad-format-experiment";
import { NativeAd } from "./native-ad";
import { APPLY_CLICKED_EVENT, clearPostApplyPending } from "./post-apply-offer";

/**
 * In-page after-apply offer (the `job_detail_after_apply` monetization surface).
 *
 * Why this exists alongside `PostApplyOffer`: the popup only appears when the
 * user returns to the tab (`visibilitychange`), which fires for a small slice of
 * applies — most apply-intent never sees an offer. This unit instead reveals an
 * engine-chosen offer INLINE, in the content flow, the moment the user clicks
 * Apply (a synchronous `ld:apply-clicked` event), so it does not depend on a
 * tab return. It renders in-content (not a fixed popup), so it reads as a
 * helpful "while you wait" suggestion rather than an interruption.
 *
 * Gated two ways so it is safe-off by default:
 *  1. The `job_detail_after_apply` surface flag in the `monetization-settings`
 *     payload — `NativeAd` self-gates through `/api/monetization/offer`, so a
 *     disabled surface returns no offer and this renders nothing.
 *  2. Only after an apply click FOR THIS job.
 *
 * When it reveals, it clears the pending flag so the tab-return popup
 * self-suppresses — an apply shows the inline unit OR the popup, never both.
 *
 * History: this was the `after-apply-inline` A/B flag. PostHog had it pinned at
 * test=100 / control=0 since 2026-07-02 and exposure tracking was off, so it
 * could not measure anything. The test arm is now unconditional and the flag
 * is retired (2026-10-05 blindspot sweep).
 */

export function AfterApplyInline({
	jobId,
	categoryId,
	locale,
}: {
	jobId: string;
	categoryId: string | null;
	locale: Locale;
}) {
	// In the `ad_breaks` arm of `ad-format-experiment` the in-flow ad break
	// (`ApplyAdBreak`) replaces this card: one paid unit per apply.
	const { adBreaks } = useAdFormatArm();
	const [applied, setApplied] = useState(false);

	useEffect(() => {
		const onApply = (e: Event) => {
			const detail = (e as CustomEvent<{ jobId?: string }>).detail;
			if (detail?.jobId === jobId) setApplied(true);
		};
		window.addEventListener(APPLY_CLICKED_EVENT, onApply);
		return () => window.removeEventListener(APPLY_CLICKED_EVENT, onApply);
	}, [jobId]);

	const show = applied && !adBreaks;

	useEffect(() => {
		if (show) clearPostApplyPending();
	}, [show]);

	if (!show) return null;

	return (
		<div className="mt-8 rounded-3xl border border-border bg-card p-5 shadow-sm">
			<p className="mb-3 text-sm font-semibold text-foreground">
				{locale === "en"
					? "Application opened — while you wait"
					: "Lamaran dibuka — sambil menunggu"}
			</p>
			<NativeAd
				surface="job_detail_after_apply"
				jobId={jobId}
				categoryId={categoryId}
				locale={locale}
			/>
		</div>
	);
}
